import { SessionTimeoutConfig } from '@shared/session'
import { AnalyticsService, CommonAnalyticsModalName } from '@ui-common/analytics'
import { useSendModalViewEvent } from '@ui-common/hooks/useSendModalViewEvent'
import { EventHandler } from '@ui-common/types'
import { getSessionStorageItem } from '@ui-common/utils/storage'
import { ModalVisualMode } from '@ui-components/containers/ModalTypes'
import { PromptModal } from '@ui-components/containers/PromptModal'
import { IndentationText } from '@ui-components/decorative/IndentationText'
import { DataSummaryItem } from '@ui-components/form/DataSummary'
import { BodyText } from '@ui-components/typography/Typography'
import { JSX, useState } from 'react'
import { useIntl } from 'react-intl'

import { CountdownTimer } from './CountdownTimer'
import { getTranslations } from './translations'
import { AUTHENTICATED_AT } from './useSessionTimer'

export const abortButton = 'AbortButton'
export const logoutButton = 'LogoutButton'

const createInitialCountdownTime = (sessionLimitInMs: number): number => {
  const authAt = getSessionStorageItem(AUTHENTICATED_AT)
  if (!authAt) {
    return 0
  }
  const durationInMs = Date.now() - Number(authAt)
  return Math.round((sessionLimitInMs - durationInMs) / 1000)
}

interface TimeoutWarningModalProps {
  onLogout: EventHandler
  analyticsService: AnalyticsService<unknown, unknown, CommonAnalyticsModalName>
  sessionTimeoutConfig: SessionTimeoutConfig
}

const ANALYTICS_NAME: CommonAnalyticsModalName = 'TimeoutWarningModal'

export const TimeoutWarningModal = ({
  onLogout,
  analyticsService,
  sessionTimeoutConfig,
}: TimeoutWarningModalProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(true)
  const { heading, leadText, timerLabel, primaryButtonLabel, secondaryButtonLabel } = getTranslations(
    useIntl().formatMessage,
    sessionTimeoutConfig,
  )

  const initialCountdownTime = createInitialCountdownTime(sessionTimeoutConfig.sessionLimitInMs)

  const handleModalClosing = () => {
    setIsOpen(false)
  }

  const handlePrimaryButtonClick = () => {
    analyticsService.sendCustomEvent(ANALYTICS_NAME, logoutButton)
    onLogout()
  }

  const handleSecondaryButtonClick = () => {
    analyticsService.sendCustomEvent(ANALYTICS_NAME, abortButton)
    setIsOpen(false)
  }

  useSendModalViewEvent(analyticsService, ANALYTICS_NAME)

  return (
    <PromptModal
      header={heading}
      primaryButtonLabel={primaryButtonLabel}
      secondaryButtonLabel={secondaryButtonLabel}
      isOpen={isOpen}
      mode={ModalVisualMode.Think}
      onPrimaryButtonClick={handlePrimaryButtonClick}
      onSecondaryButtonClick={handleSecondaryButtonClick}
      onClose={handleModalClosing}
    >
      <BodyText>{leadText}</BodyText>
      <IndentationText>
        <DataSummaryItem
          id="session-timer"
          label={timerLabel}
          value={<CountdownTimer initialTimeInSeconds={initialCountdownTime} onComplete={onLogout} />}
        />
      </IndentationText>
    </PromptModal>
  )
}

export default TimeoutWarningModal
